import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Star, ArrowUpRight, MessageSquareQuote } from "lucide-react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";
import { api } from "@/lib/api";

const SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

function Stars({ value, onChange, size = "md" }) {
  const [hover, setHover] = useState(0);
  const interactive = typeof onChange === "function";
  const px = size === "lg" ? 32 : size === "sm" ? 14 : 20;
  return (
    <div
      className="inline-flex items-center gap-1"
      role={interactive ? "radiogroup" : undefined}
      aria-label={interactive ? "Star rating" : `${value} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        const El = interactive ? "button" : "span";
        return (
          <El
            key={n}
            type={interactive ? "button" : undefined}
            role={interactive ? "radio" : undefined}
            aria-checked={interactive ? value === n : undefined}
            onMouseEnter={interactive ? () => setHover(n) : undefined}
            onMouseLeave={interactive ? () => setHover(0) : undefined}
            onClick={interactive ? () => onChange(n) : undefined}
            className={interactive ? "focus:outline-none focus:ring-2 focus:ring-[var(--jb-rust)] rounded" : ""}
            data-testid={interactive ? `star-${n}` : undefined}
          >
            <Star
              width={px}
              height={px}
              className={active ? "text-[var(--jb-rust)] fill-[var(--jb-rust)]" : "text-[var(--jb-navy)]/25"}
              strokeWidth={1.5}
            />
          </El>
        );
      })}
    </div>
  );
}

function ReviewCard({ r, i }) {
  return (
    <motion.blockquote
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: i * 0.05 }}
      className="bg-white border border-[var(--jb-line)] rounded-2xl p-8 md:p-10 shadow-sm relative"
      data-testid={`review-card-${r.id}`}
    >
      <MessageSquareQuote className="absolute top-6 right-6 w-6 h-6 text-[var(--jb-rust)]/25" />
      <Stars value={r.rating} size="sm" />
      <p className="serif italic text-[19px] md:text-[22px] leading-[1.45] text-[var(--jb-navy)] mt-4">
        "{r.body}"
      </p>
      <footer className="mt-6 pt-5 border-t border-[var(--jb-line)] flex items-center justify-between">
        <span className="mono-cap text-[var(--jb-navy)]">{r.name}</span>
        <span className="mono-cap text-[10px] text-[var(--jb-muted)]">
          {new Date(r.approved_at || r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
        </span>
      </footer>
    </motion.blockquote>
  );
}

function ReviewsList({ reviews, loading }) {
  const avg = useMemo(() => {
    if (!reviews.length) return null;
    return (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);
  }, [reviews]);

  if (loading) {
    return <div className="mono-cap text-[var(--jb-muted)] py-10">Loading reviews…</div>;
  }
  if (reviews.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[var(--jb-line)] p-10 text-center shadow-sm">
        <div className="serif text-2xl text-[var(--jb-navy)]">Be the first to review Jaybliss.</div>
        <p className="mt-3 text-[var(--jb-muted)] text-[15px]">Your kind words help other Belfast movers choose with confidence.</p>
      </div>
    );
  }
  return (
    <>
      {avg && (
        <div className="flex items-baseline gap-4 mb-8">
          <span className="serif text-6xl text-[var(--jb-navy)]">{avg}</span>
          <div>
            <Stars value={Math.round(Number(avg))} size="md" />
            <div className="mono-cap text-[11px] text-[var(--jb-muted)] mt-1">
              {reviews.length} customer review{reviews.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-5">
        {reviews.map((r, i) => <ReviewCard key={r.id} r={r} i={i} />)}
      </div>
    </>
  );
}

function SubmissionForm({ onSubmitted }) {
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [done, setDone] = useState(false);
  const captchaRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name.");
    if (!rating) return toast.error("Please choose a star rating.");
    if (body.trim().length < 10) return toast.error("Please write at least 10 characters.");
    if (SITE_KEY && !captchaToken) return toast.error("Please complete the captcha.");
    setLoading(true);
    try {
      await api.post("/reviews", { name: name.trim(), rating, body: body.trim(), captcha_token: captchaToken });
      setDone(true);
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please try again shortly.");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken("");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-[var(--jb-navy)] text-[var(--jb-bone)] rounded-2xl p-10 shadow-sm relative overflow-hidden"
        data-testid="review-done"
      >
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative">
          <div className="mono-cap text-[var(--jb-rust)] mb-3">Thank you ✓</div>
          <div className="serif text-3xl md:text-4xl leading-tight">
            Review received. <br />
            <span className="italic text-[var(--jb-rust)]">Awaiting moderation.</span>
          </div>
          <p className="mt-6 text-[var(--jb-bone)]/70 max-w-md">
            We read every review personally. Yours will appear on the site once approved — usually within a business day.
          </p>
        </div>
      </motion.div>
    );
  }

  const inputCls =
    "w-full bg-transparent border-0 border-b border-[var(--jb-navy)]/25 py-3.5 text-[16px] text-[var(--jb-navy)] placeholder:text-[var(--jb-muted)] focus:outline-none focus:border-[var(--jb-rust)] transition-colors";

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white border border-[var(--jb-line)] rounded-2xl p-8 md:p-10 shadow-sm"
      data-testid="review-form"
    >
      <div className="mono-cap text-[var(--jb-rust)] mb-3">Leave a review</div>
      <h3 className="serif text-3xl md:text-4xl text-[var(--jb-navy)] leading-tight">
        Share your <span className="italic">Jaybliss experience.</span>
      </h3>
      <p className="text-[14px] text-[var(--jb-muted)] mt-2 max-w-md">
        Honest, first-name reviews only. Nothing is published until we approve it.
      </p>

      <div className="mt-8 space-y-5">
        <div>
          <label className="mono-cap text-[var(--jb-muted)]">Your name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
            placeholder="e.g. Ciara O'Neill"
            data-testid="review-name"
            maxLength={80}
          />
        </div>
        <div>
          <label className="mono-cap text-[var(--jb-muted)] block mb-2">Star rating</label>
          <Stars value={rating} onChange={setRating} size="lg" />
        </div>
        <div>
          <label className="mono-cap text-[var(--jb-muted)]">Your review</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            className={`${inputCls} resize-none`}
            placeholder="How did we do? What went well?"
            data-testid="review-body"
            maxLength={1200}
          />
          <div className="mono-cap text-[10px] text-[var(--jb-muted)] mt-1 text-right">{body.length} / 1200</div>
        </div>
        {SITE_KEY && (
          <div data-testid="review-captcha">
            <HCaptcha
              ref={captchaRef}
              sitekey={SITE_KEY}
              onVerify={(t) => setCaptchaToken(t)}
              onExpire={() => setCaptchaToken("")}
              onError={() => setCaptchaToken("")}
              theme="light"
            />
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="cta-primary bg-[var(--jb-navy)] text-[var(--jb-bone)] mono-cap px-7 py-4 mt-8 inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="submit-review"
      >
        {loading ? "Sending…" : "Submit review"}
        <ArrowUpRight className="w-4 h-4" />
      </button>
    </motion.form>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <main className="bg-[var(--jb-bone)] min-h-screen" data-testid="reviews-page">
      <SmoothScroll />
      <Header />

      <section className="pt-[130px] md:pt-[170px] pb-14">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="mono-cap text-[var(--jb-rust)] mb-4">(Reviews)</div>
          <h1 className="serif text-[clamp(48px,8vw,128px)] leading-[0.95] tracking-tight text-[var(--jb-navy)]">
            Real words from <br />
            <span className="italic">real movers.</span>
          </h1>
          <p className="mt-8 max-w-[560px] text-[16px] leading-[1.6] text-[var(--jb-muted)]">
            Every review here is from a genuine customer we've moved.
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <ReviewsList reviews={reviews} loading={loading} />
          </div>
          <div className="md:col-span-5">
            <div className="md:sticky md:top-28">
              <SubmissionForm onSubmitted={load} />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
